import { Box, Button, Card, CardContent, CardHeader, Grid, MenuItem, TextField, Typography } from '@mui/material';
import api from 'api';
import BodyTemplate from 'components/_layout/navigation/BodyTemplate';
import ContextLink from 'components/_base/ContextLink';
import { useContextRedirection, useNotification } from 'hooks';
import { PrescriptionProfileListResponse } from 'models/prescription.model';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GlobalState } from 'store/reducers';
import RoutePaths from 'utils/RoutePaths';

const Settings: FC = () => {
  const { t } = useTranslation();
  const { notification } = useNotification();
  const navigate = useContextRedirection();

  const currentUser = useSelector((state: GlobalState) => state.session.authenticateUser);

  const [prescriptionProfiles, setPrescriptionProfiles] = useState<PrescriptionProfileListResponse>([]);
  const [pdfData, setPdfData] = useState<Blob>();

  useEffect(() => {
    api.prescriptions
      .getEditablePrescriptionsProfilesByDoctorId({
        ms_id: currentUser!.id,
      })
      .then((response: PrescriptionProfileListResponse) => {
        setPrescriptionProfiles(response);
      })
      .catch(() => notification(t('prescriptions.profiles.notification.error'), 'error'));
  }, [currentUser, notification, t]);

  useEffect(() => {
    api.prescriptions
      .getPrescriptionFileForDoctor()
      .then((data) => {
        setPdfData(data);
      })
      .catch(() => notification(t('prescriptions.types.error'), 'error'));
  }, [notification, t]);

  const files = pdfData && new Blob([pdfData], { type: 'application/pdf' });
  const fileURL = files && URL.createObjectURL(files);

  return (
    <BodyTemplate title={t('bodyTemplate.doctor.settings')}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <Grid container>
              <Grid item xs={12}>
                <CardHeader
                  title={
                    <Typography variant='h2' mb={2}>
                      {t('prescriptions.profiles.title')}
                    </Typography>
                  }
                />
                <CardContent>
                  <Typography variant='body1' mb={4}>
                    {t('prescriptions.profiles.subtitle')}
                  </Typography>
                  <TextField select variant='filled' label={`${t('prescriptions.profiles.field.profile_name')}`} defaultValue={''} fullWidth>
                    {prescriptionProfiles.map(({ id, name }, i) => (
                      <MenuItem
                        value={id}
                        key={`profile-${id}`}
                        onClick={() => {
                          navigate(RoutePaths['DOCTOR_PRESCRIPTION_PROFILES_UPDATE'](id));
                        }}
                      >
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '20px',
                      mt: 4,
                    }}
                  >
                    <ContextLink to='DOCTOR_PRESCRIPTION_PROFILES_NEW'>
                      <Button type='button' variant='contained'>
                        {t('prescriptions.profiles.new_profile')}
                      </Button>
                    </ContextLink>
                  </Box>
                </CardContent>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <Grid container>
              <Grid item xs={6}>
                <CardHeader
                  title={
                    <Typography variant='h2' mb={2}>
                      {t('prescriptions.types.title')}
                    </Typography>
                  }
                />
                <CardContent>
                  <Typography variant='body1'>{t('prescriptions.types.subtitle')}</Typography>
                </CardContent>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    display: 'flex',
                    height: '100%',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                  }}
                >
                  <a href={fileURL} download style={{ textDecoration: 'none' }}>
                    <Button type='submit' variant='contained'>
                      {t('common.action.pdfDownload')}
                    </Button>
                  </a>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </BodyTemplate>
  );
};

export default Settings;
